import { Flex, Tooltip, Typography } from "antd";
import { ColumnType } from "antd/es/table";

import {
  UserListEntity,
  USER_SCOPE_TEXTS,
  UserStatus,
  REVERSE_ORDER_MAP,
  getEnumKeys,
  CompanyUserDto,
  COMPANY_TYPES,
} from "interfaces";
import { UserListStore } from "stores/UserListStore";
import { SquareIcon, Status } from "ui";
import { dateFormat, getCompanyType, getFilterIcon, getPhone, getSortIcon } from "utils";
import { ReactComponent as InfoIcon } from "images/menuIcons/Info.svg";
import { ReactComponent as UserIcon } from "images/headerIcons/union.svg";
import styles from "./users.module.css";
import { ReactComponent as DotIcon } from "images/Steps/dot.svg";

export const getColumns = (userList: UserListStore): Array<ColumnType<UserListEntity>> => {
  return [
    {
      dataIndex: "surname",
      key: "surname",
      title: "ФИО",
      width: 260,
      sorter: { multiple: 1 },
      sortOrder: userList?.sorters?.name && REVERSE_ORDER_MAP[userList.sorters.name],
      sortIcon: getSortIcon,
      className: `text-clamp-ellipsis ${styles.userFullName}`,
      render: (_: any, record: UserListEntity) => {
        return (
          <Flex gap="small" align="center">
            <SquareIcon icon={UserIcon} size="xl" squareSize="3xl" squareBg />
            <Flex vertical gap={2}>
              <span>
                {record.surname} {record.name} {record.secondName ?? ""}
              </span>
              {getPhone(record.phone)}
            </Flex>
          </Flex>
        );
      },
    },

    {
      dataIndex: "companyUserDto",
      key: "companyName",
      title: "Компания",
      width: 270,
      className: `text-clamp-ellipsis ${styles.companyName}`,
      render: (companyUserDto: CompanyUserDto[]) => {
        if (companyUserDto.length === 1) {
          const companyName = companyUserDto[0].companyName ?? "";
          const ogrnPrefix =
            getCompanyType(companyUserDto[0].ogrn) === COMPANY_TYPES.IP ? "ОГРНИП:" : "ОГРН:";
          return (
            <Flex vertical gap={2}>
              <Tooltip trigger="hover" placement="topLeft" title={companyName}>
                <span>{companyName}</span>
              </Tooltip>
              <Flex align="center" gap="small">
                <Typography.Text className={styles.statusDateText}>ИНН: {companyUserDto[0].inn}</Typography.Text>
                <DotIcon className={styles.statusDateText} />
                <Typography.Text className={styles.statusDateText}>
                  {ogrnPrefix} {companyUserDto[0].ogrn}
                </Typography.Text>
              </Flex>
            </Flex>
          );
        } else if (companyUserDto.length > 1) {
          return (
            <Tooltip trigger="hover" placement="topLeft" title="Пользователь прикреплен к нескольким компаниям">
              <span className="text-description flex-center">
                Несколько компаний <InfoIcon className="text-icon" />
              </span>
            </Tooltip>
          );
        }
        return "";
      },
    },

    {
      dataIndex: "statusDate",
      key: "statuses",
      title: "Статус",
      align: "right",
      width: 130,
      filterMultiple: true,
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      defaultFilteredValue: userList && userList.queryParams["statuses"],
      filtered: userList && "status" in userList.queryParams,
      filters: getEnumKeys(USER_SCOPE_TEXTS.texts).map((key) => ({
        text: USER_SCOPE_TEXTS.texts[key],
        value: key,
      })),
      sorter: { multiple: 2 },
      sortOrder: userList.sorters.statusDate && REVERSE_ORDER_MAP[userList.sorters.statusDate],
      sortIcon: getSortIcon,
      render: (_: any, record: any) => {
        return (
          <Flex vertical gap={2} align="end">
            <Status
              color={USER_SCOPE_TEXTS.colors[record.status as UserStatus]}
              children={USER_SCOPE_TEXTS.texts[record.status as UserStatus]}
            />
            <Typography.Text className={styles.statusDateText}>c {dateFormat(record.statusDate)}</Typography.Text>
          </Flex>
        );
      },
    },
  ].filter((c) => !!c) as ColumnType<UserListEntity>[];
};
